import React, {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibTextarea } from '@chewy/kib-fields-new-react'
import { KibMicIcon, KibMicOffIcon, KibSendIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'

import { useSpeechRecognition } from '~/hooks/useSpeechRecognition'

import ListeningIndicator from './ListeningIndicator'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './ChatInput.module.scss'

export interface ChatInputProps {
  disabled?: boolean
  onSendMessage: (message: string) => void
  placeholder?: string
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder = '',
}) => {
  const [message, setMessage] = useState('')
  const [baseMessage, setBaseMessage] = useState('') // Message before starting speech
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { t } = useTranslation('Agent')

  const {
    audioStream,
    error: speechError,
    isListening,
    isSupported: isSpeechSupported,
    startListening,
    stopListening,
    transcript,
  } = useSpeechRecognition({
    autoStop: true,
    onTranscriptComplete: (finalTranscript) => {
      const combined = baseMessage
        ? `${baseMessage} ${finalTranscript}`
        : finalTranscript
      setMessage(combined.trim())
      setBaseMessage('')
    },
  })

  // Update message field with interim transcript results
  useEffect(() => {
    if (isListening && transcript) {
      const combinedMessage = baseMessage
        ? `${baseMessage} ${transcript}`
        : transcript
      setMessage(combinedMessage.trim())
    }
  }, [transcript, isListening, baseMessage])

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim())
      setMessage('')
      setBaseMessage('')
    }
  }

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.style.height = 'auto'

    const maxHeight = 160
    const newHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = `${newHeight}px`
  }, [])

  const handleMessageChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setMessage(e.target.value)
  }

  useEffect(() => {
    adjustTextareaHeight()
  }, [message, adjustTextareaHeight])

  const handleKeyPress = (
    event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const handleMicClick = () => {
    if (isListening) {
      stopListening()
    } else {
      setBaseMessage(message)
      startListening()
    }
  }

  const isMicDisabled = disabled || speechError === 'microphone-denied'
  const micButtonTitle =
    speechError === 'microphone-denied'
      ? t('Agent:MICROPHONE_ACCESS_DENIED')
      : isListening
        ? t('Agent:STOP_LISTENING')
        : t('Agent:START_LISTENING')

  return (
    <div className={styles.inputContainer}>
      <div className={styles.inputWrapper}>
        <div className={styles.messageInput}>
          <KibTextarea
            className={styles.textarea}
            disabled={disabled || isListening}
            label=""
            placeholder={placeholder}
            renderControl={(attrs) => (
              <textarea
                {...attrs}
                ref={textareaRef}
                style={{
                  resize: 'none',
                  overflow: 'auto',
                  maxHeight: '160px',
                }}
              />
            )}
            rows={1}
            value={message}
            onChange={handleMessageChange}
            onKeyPress={handleKeyPress}
          />
        </div>
        <div className={styles.buttonContainer}>
          <div
            className={classNames(styles.listeningIndicatorWrapper, {
              [styles.hidden]: !isListening,
            })}
          >
            <ListeningIndicator audioStream={audioStream} />
          </div>
          {isSpeechSupported && (
            <KibButtonNew
              iconOnly
              aria-label={micButtonTitle}
              disabled={isMicDisabled}
              icon={isListening ? <KibMicOffIcon /> : <KibMicIcon />}
              size="small"
              title={micButtonTitle}
              onClick={handleMicClick}
            />
          )}
          <div
            className={classNames(styles.sendButtonWrapper, {
              [styles.hidden]: isListening,
            })}
          >
            <KibButtonNew
              iconOnly
              aria-label={t('Agent:SEND_MESSAGE')}
              disabled={disabled || !message.trim()}
              icon={<KibSendIcon />}
              size="small"
              onClick={handleSend}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatInput
